import React, { useEffect, useState } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Button, 
  Grid, 
  Card, 
  CardContent,
  CardMedia,
  Avatar,
  Rating,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Divider
} from '@mui/material';
import { Link } from 'react-router-dom';
import { useContent } from '../context/ContentContext';
import { styled } from '@mui/material/styles';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import PeopleIcon from '@mui/icons-material/People';
import VerifiedIcon from '@mui/icons-material/Verified';
import ConstructionIcon from '@mui/icons-material/Construction';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import CloseIcon from '@mui/icons-material/Close';
import ContactModal from '../components/ContactModal';

// Styled components - Heron Power inspired minimalist design
const HeroSection = styled(Box)(({ theme }) => ({
  position: 'relative',
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(0),
  color: '#fff',
  backgroundColor: '#000',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 1,
  }
}));

const SectionTitle = styled(Typography)(({ theme, light }) => ({
  marginBottom: theme.spacing(2),
  fontWeight: 500,
  color: light ? '#fff' : '#000',
  letterSpacing: '-0.02em',
}));

const SectionSubtitle = styled(Typography)(({ theme, light }) => ({
  marginBottom: theme.spacing(6),
  color: light ? 'rgba(255, 255, 255, 0.7)' : '#666',
  fontWeight: 400,
  maxWidth: '600px',
}));

const FeatureCard = styled(Card)(({ theme }) => ({
  height: '100%',
  border: 'none',
  boxShadow: 'none',
  backgroundColor: 'transparent',
  transition: 'opacity 0.2s ease-in-out',
  '&:hover': {
    opacity: 0.7,
  }
}));

const FeatureIcon = styled(Box)(({ theme }) => ({
  width: 48,
  height: 48,
  borderRadius: 0,
  backgroundColor: '#000',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: theme.spacing(3),
  color: '#fff',
}));

const ServiceIcon = styled(Box)(({ theme }) => ({
  width: 80,
  height: 80,
  borderRadius: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: theme.spacing(3),
  overflow: 'hidden',
  backgroundColor: '#f8f8f8',
}));

const StatBox = styled(Box)(({ theme }) => ({
  backgroundColor: 'transparent',
  padding: theme.spacing(3, 0),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  textAlign: 'left',
  borderTop: '1px solid rgba(0, 0, 0, 0.1)',
}));

const StatValue = styled(Typography)(({ theme }) => ({
  fontWeight: 500,
  color: '#000',
  fontSize: '3rem',
  letterSpacing: '-0.02em',
  marginBottom: theme.spacing(0.5),
}));

const StatLabel = styled(Typography)(({ theme }) => ({
  fontWeight: 400,
  color: '#666',
  fontSize: '14px',
  textTransform: 'uppercase',
  letterSpacing: '0.1em',
}));

const TestimonialCard = styled(Card)(({ theme }) => ({
  height: '100%',
  border: '1px solid rgba(0, 0, 0, 0.08)',
  boxShadow: 'none',
  borderRadius: 0,
  transition: 'border-color 0.2s ease',
  '&:hover': {
    borderColor: '#000',
  }
}));

const ProjectCard = styled(Card)(({ theme }) => ({
  height: '100%',
  borderRadius: 0,
  overflow: 'hidden',
  position: 'relative',
  border: 'none',
  boxShadow: 'none',
  '&:hover .project-overlay': {
    opacity: 1,
  },
  '&:hover img': {
    transform: 'scale(1.02)',
  }
}));

const ProjectOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  backgroundColor: 'rgba(0, 0, 0, 0.8)',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  padding: theme.spacing(4),
  opacity: 0,
  transition: 'opacity 0.3s ease',
}));

const ContactSection = styled(Box)(({ theme }) => ({
  backgroundColor: '#000',
  color: '#fff',
  padding: theme.spacing(12, 0),
  position: 'relative',
  overflow: 'hidden',
}));

const getFeatureIcon = (iconName) => {
  switch(iconName) {
    case 'people':
      return <PeopleIcon fontSize="large" />;
    case 'verified':
      return <VerifiedIcon fontSize="large" />;
    case 'construction':
      return <ConstructionIcon fontSize="large" />;
    case 'support_agent':
      return <SupportAgentIcon fontSize="large" />;
    default:
      return null;
  }
};

// Animations
const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.6 }
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2
    }
  }
};

function Home() {
  const { content } = useContent();
  const { home } = content;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  const { t, i18n } = useTranslation();
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [serviceDialogOpen, setServiceDialogOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectDialogOpen, setProjectDialogOpen] = useState(false);
  const currentLanguage = i18n.language;

  const handleOpenContactModal = () => {
    setContactModalOpen(true);
  };

  const handleCloseContactModal = () => {
    setContactModalOpen(false);
  };

  const handleOpenServiceDialog = (service) => {
    setSelectedService(service);
    setServiceDialogOpen(true);
    console.log('Selected service:', service);
    console.log('Full Description:', service.fullDescription);
    console.log('Full Description ZH:', service.fullDescription_zh);
  };

  const handleCloseServiceDialog = () => {
    setServiceDialogOpen(false);
  };

  const handleOpenProjectDialog = (project) => {
    setSelectedProject(project);
    setProjectDialogOpen(true);
    console.log('Selected project:', project);
  };

  const handleCloseProjectDialog = () => {
    setProjectDialogOpen(false);
  };

  // 处理从其他页面跳转过来并需要滚动到特定元素的情况
  useEffect(() => {
    const scrollToElementId = sessionStorage.getItem('scrollToElementId');
    if (scrollToElementId) {
      // 清除会话存储中的标记
      sessionStorage.removeItem('scrollToElementId');
      
      // 延迟一点以确保DOM已经渲染
      setTimeout(() => {
        const targetElement = document.getElementById(scrollToElementId);
        if (targetElement) {
          targetElement.scrollIntoView({ behavior: 'smooth' });
        } else {
          console.warn(`Element with id '${scrollToElementId}' not found`);
        }
      }, 300);
    }
  }, []);

  return (
    <Box>
      {/* Hero Section - Heron Power inspired full-screen minimalist design */}
      <HeroSection
        sx={{
          backgroundImage: 'url(/images/hero-bg.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
          <Box sx={{
            textAlign: 'center',
            maxWidth: '900px',
            mx: 'auto',
            py: { xs: 10, md: 0 }
          }}>
            <motion.div
              initial="hidden"
              animate="visible"
              variants={fadeIn}
            >
              <Typography
                variant="h1"
                sx={{
                  mb: 3,
                  fontWeight: 500,
                  letterSpacing: '-0.03em',
                  fontSize: { xs: '2.5rem', sm: '3.5rem', md: '4.5rem', lg: '5.5rem' },
                  lineHeight: 1.1,
                }}
              >
                {currentLanguage === 'zh' ? home.hero.title_zh : (home.hero.title_en || home.hero.title)}
              </Typography>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.6 }}
            >
              <Typography
                variant="h5"
                sx={{
                  mb: 6,
                  fontWeight: 400,
                  color: 'rgba(255, 255, 255, 0.8)',
                  fontSize: { xs: '1rem', md: '1.25rem' },
                  maxWidth: '700px',
                  mx: 'auto',
                  lineHeight: 1.7
                }}
              >
                {currentLanguage === 'zh' ? home.hero.description_zh : (home.hero.description_en || home.hero.description)}
              </Typography>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.6 }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 2 }}>
                <Button
                  component={Link}
                  to={home.hero.cta.primary.path}
                  variant="contained"
                  size="large"
                  sx={{
                    px: 5,
                    py: 1.5,
                    fontSize: '14px',
                    fontWeight: 500,
                    backgroundColor: '#fff',
                    color: '#000',
                    borderRadius: 0,
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    }
                  }}
                >
                  {currentLanguage === 'zh' ? home.hero.cta.primary.text_zh : (home.hero.cta.primary.text_en || home.hero.cta.primary.text)}
                </Button>
                <Button
                  component={Link}
                  to={home.hero.cta.secondary.path}
                  variant="outlined"
                  size="large"
                  sx={{
                    px: 5,
                    py: 1.5,
                    fontSize: '14px',
                    fontWeight: 500,
                    borderRadius: 0,
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                    color: '#fff',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      borderColor: '#fff',
                    }
                  }}
                >
                  {currentLanguage === 'zh' ? home.hero.cta.secondary.text_zh : (home.hero.cta.secondary.text_en || home.hero.cta.secondary.text)}
                </Button>
              </Box>
            </motion.div>
          </Box>
        </Container>

        {/* Scroll indicator */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 40,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 2,
          }}
        >
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ repeat: Infinity, duration: 2 }}
          >
            <Box
              sx={{
                width: 24,
                height: 40,
                border: '2px solid rgba(255,255,255,0.5)',
                borderRadius: 12,
                display: 'flex',
                justifyContent: 'center',
                pt: 1
              }}
            >
              <Box
                sx={{
                  width: 4,
                  height: 8,
                  backgroundColor: '#fff',
                  borderRadius: 2
                }}
              />
            </Box>
          </motion.div>
        </Box>
      </HeroSection>

      {/* Features Section - Minimalist grid layout */}
      <Box sx={{ py: { xs: 10, md: 15 }, backgroundColor: '#fff' }}>
        <Container maxWidth="lg">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={staggerContainer}
          >
            <Grid container spacing={{ xs: 4, md: 8 }}>
              {home.features.map((feature, index) => {
                return (
                  <Grid item xs={12} sm={6} md={3} key={index}>
                    <motion.div variants={fadeIn}>
                      <FeatureCard elevation={0}>
                        <CardContent sx={{ p: 0 }}>
                          <FeatureIcon>
                            {getFeatureIcon(feature.icon)}
                          </FeatureIcon>
                          <Typography
                            variant="h6"
                            sx={{
                              mb: 2,
                              fontWeight: 500,
                              fontSize: '18px',
                              letterSpacing: '-0.01em'
                            }}
                          >
                            {currentLanguage === 'zh' ? feature.title_zh : (feature.title_en || feature.title)}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: '#666',
                              lineHeight: 1.7,
                              fontSize: '14px'
                            }}
                          >
                            {currentLanguage === 'zh' ? feature.description_zh : (feature.description_en || feature.description)}
                          </Typography>
                        </CardContent>
                      </FeatureCard>
                    </motion.div>
                  </Grid>
                );
              })}
            </Grid>
          </motion.div>
        </Container>
      </Box>

      {/* About Section - Clean layout with stats */}
      <Box id="about" sx={{ py: { xs: 10, md: 15 }, backgroundColor: '#f8f8f8' }}>
        <Container maxWidth="lg">
          <Grid container spacing={{ xs: 6, md: 10 }} alignItems="center">
            <Grid item xs={12} md={6}>
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={fadeIn}
              >
                <Typography
                  variant="overline"
                  sx={{
                    color: '#666',
                    mb: 2,
                    display: 'block',
                    letterSpacing: '0.15em'
                  }}
                >
                  {currentLanguage === 'zh' ? '关于我们' : 'ABOUT US'}
                </Typography>
                <SectionTitle variant="h2" sx={{ fontSize: { xs: '2rem', md: '2.5rem' } }}>
                  {currentLanguage === 'zh' ? home.about.title_zh : (home.about.title_en || home.about.title)}
                </SectionTitle>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 6,
                    color: '#666',
                    lineHeight: 1.8,
                    fontSize: '16px'
                  }}
                >
                  {currentLanguage === 'zh' ? home.about.description_zh : (home.about.description_en || home.about.description)}
                </Typography>

                <Grid container spacing={3}>
                  {home.about.statistics.map((stat, index) => (
                    <Grid item xs={6} key={index}>
                      <StatBox>
                        <StatValue variant="h3">
                          {stat.value}
                        </StatValue>
                        <StatLabel variant="body2">
                          {currentLanguage === 'zh' ? stat.label_zh : (stat.label_en || stat.label)}
                        </StatLabel>
                      </StatBox>
                    </Grid>
                  ))}
                </Grid>
              </motion.div>
            </Grid>
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.8 }}
              >
                <Box
                  component="img"
                  src={home.about.image}
                  alt="About Us"
                  sx={{
                    width: '100%',
                    height: { xs: '300px', md: '500px' },
                    objectFit: 'cover',
                  }}
                />
              </motion.div>
            </Grid>
          </Grid>

          {/* Projects - Power Sites (moved into About section) */}
          <Box sx={{ mt: { xs: 8, md: 12 } }}>
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              variants={fadeIn}
            >
              <Typography
                variant="overline"
                sx={{
                  color: '#666',
                  mb: 2,
                  display: 'block',
                  letterSpacing: '0.15em'
                }}
              >
                {currentLanguage === 'zh' ? '项目案例' : 'PROJECTS'}
              </Typography>
              <SectionTitle variant="h2" sx={{ fontSize: { xs: '2rem', md: '2.5rem' }, mb: 2 }}>
                {currentLanguage === 'zh' ? '电力站点' : 'Power Generation Sites'}
              </SectionTitle>
              <SectionSubtitle sx={{ maxWidth: '500px', mb: 6 }}>
                {currentLanguage === 'zh' ? '高压电力基础设施项目' : 'High-Voltage Electrical Infrastructure Projects'}
              </SectionSubtitle>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              variants={staggerContainer}
            >
              <Grid container spacing={3}>
                {[
                  { name: 'Dalton GA', location: 'Georgia', capacity: '300', unit: 'MVA', voltage: '230kV', year: '2025', coords: '34.7698° N, 84.9702° W', lat: 34.7698, lng: -84.9702 },
                  { name: 'Denton TX', location: 'Texas', capacity: '250', unit: 'MW', voltage: '230kV', year: '2025', coords: '33.2148° N, 97.1331° W', lat: 33.2148, lng: -97.1331 },
                  { name: 'Jigowatt OK', location: 'Oklahoma', capacity: '200', unit: 'MW', voltage: '230kV', year: '2025', coords: '35.4676° N, 97.5348° W', lat: 35.4676, lng: -97.5348 },
                  { name: 'Tuco TX', location: 'Texas', capacity: '1', unit: 'GW', voltage: '345kV', year: '2024', coords: '31.5000° N, 98.5000° W', lat: 31.5000, lng: -98.5000 },
                  { name: 'Duncan OK', location: 'Oklahoma', capacity: '42', unit: 'MW', voltage: '138kV', year: '2024', coords: '34.5023° N, 97.9578° W', lat: 34.5023, lng: -97.9578 },
                  { name: 'Alabama', location: 'United States', capacity: '130', unit: 'MV', voltage: '115kV', year: '2026', coords: '32.8067° N, 86.7911° W', lat: 32.8067, lng: -86.7911 },
                ].map((site, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <motion.div variants={fadeIn}>
                      <Box
                        sx={{
                          backgroundColor: '#fff',
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          transition: 'all 0.2s ease',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                          '&:hover': {
                            backgroundColor: '#fafafa',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                          }
                        }}
                      >
                        {/* Google Maps Embed */}
                        <Box
                          sx={{
                            width: '100%',
                            height: 180,
                            overflow: 'hidden',
                            position: 'relative',
                          }}
                        >
                          <iframe
                            title={`Map of ${site.name}`}
                            src={`https://www.google.com/maps/embed?pb=!1m14!1m12!1m3!1d50000!2d${site.lng}!3d${site.lat}!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!5e0!3m2!1sen!2sus!4v1600000000000!5m2!1sen!2sus`}
                            width="100%"
                            height="100%"
                            style={{ border: 0 }}
                            allowFullScreen=""
                            loading="lazy"
                            referrerPolicy="no-referrer-when-downgrade"
                          />
                        </Box>

                        {/* Project Info */}
                        <Box sx={{ p: 3, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                          <Typography
                            variant="overline"
                            sx={{
                              color: '#666',
                              letterSpacing: '0.1em',
                              mb: 1
                            }}
                          >
                            {site.location}
                          </Typography>
                          <Typography
                            variant="h5"
                            sx={{
                              fontWeight: 500,
                              mb: 2,
                              fontSize: { xs: '1.25rem', md: '1.5rem' },
                              color: '#000'
                            }}
                          >
                            {site.name}
                          </Typography>
                          <Box sx={{ mt: 'auto' }}>
                            <Box sx={{ display: 'flex', gap: 4, mb: 2 }}>
                              <Box>
                                <Typography variant="h4" sx={{ fontWeight: 500, fontSize: '2rem', lineHeight: 1, color: '#000' }}>
                                  {site.capacity}
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#666', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                                  {site.unit}
                                </Typography>
                              </Box>
                              <Box>
                                <Typography variant="h6" sx={{ fontWeight: 500, fontSize: '1.1rem', color: '#000' }}>
                                  {site.voltage}
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#666', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                                  {currentLanguage === 'zh' ? '电压' : 'Voltage'}
                                </Typography>
                              </Box>
                            </Box>
                            <Typography variant="body2" sx={{ color: '#666', fontSize: '12px' }}>
                              {site.year} · {site.coords}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </motion.div>
                  </Grid>
                ))}
              </Grid>
            </motion.div>
          </Box>
        </Container>
      </Box>

      {/* Services Section - AI Roadmap */}
      <Box id="services" sx={{ py: { xs: 10, md: 15 }, backgroundColor: '#fff' }}>
        <Container maxWidth="lg">
          <Box sx={{ mb: 8, textAlign: 'center' }}>
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              variants={fadeIn}
            >
              <Typography
                variant="overline"
                sx={{
                  color: '#666',
                  mb: 2,
                  display: 'block',
                  letterSpacing: '0.15em'
                }}
              >
                {currentLanguage === 'zh' ? '服务' : 'SERVICES'}
              </Typography>
              <Typography
                variant="h2"
                sx={{
                  fontWeight: 500,
                  letterSpacing: '-0.02em',
                  fontSize: { xs: '2rem', md: '2.5rem' },
                  mb: 2
                }}
              >
                {currentLanguage === 'zh' ? 'AI电力设备与解决方案' : 'AI Power Equipment & Solutions'}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: '#666',
                  maxWidth: '600px',
                  mx: 'auto',
                  fontSize: '16px',
                  lineHeight: 1.7
                }}
              >
                {currentLanguage === 'zh'
                  ? '从解决方案集成商到AI原生电力公司的战略发展规划'
                  : 'Strategic development plan from solutions integrator to AI-native power company'}
              </Typography>
            </motion.div>
          </Box>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={staggerContainer}
          >
            <Grid container spacing={0}>
              {/* Phase 1 */}
              <Grid item xs={12} md={6}>
                <motion.div variants={fadeIn}>
                  <Box
                    sx={{
                      p: { xs: 4, md: 5 },
                      height: '100%',
                      borderRight: { xs: 'none', md: '1px solid rgba(0,0,0,0.08)' },
                      borderBottom: '1px solid rgba(0,0,0,0.08)',
                      transition: 'background-color 0.2s ease',
                      '&:hover': {
                        backgroundColor: '#f8f8f8',
                      }
                    }}
                  >
                    <Box
                      sx={{
                        width: 64,
                        height: 64,
                        backgroundColor: '#000',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 3,
                        color: '#fff',
                      }}
                    >
                      <ConstructionIcon sx={{ fontSize: 32 }} />
                    </Box>

                    <Typography
                      variant="overline"
                      sx={{ color: '#666', letterSpacing: '0.1em', mb: 1, display: 'block' }}
                    >
                      {currentLanguage === 'zh' ? '第一阶段：奠定基础' : 'Phase 1: Foundation'}
                    </Typography>

                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 500,
                        mb: 1,
                        fontSize: { xs: '1.5rem', md: '1.75rem' },
                        letterSpacing: '-0.01em'
                      }}
                    >
                      {currentLanguage === 'zh' ? 'AI解决方案集成商' : 'AI-Focused Solutions Integrator'}
                    </Typography>

                    <Typography
                      sx={{
                        color: '#000',
                        fontWeight: 600,
                        fontSize: '14px',
                        mb: 3,
                        display: 'inline-block',
                        backgroundColor: '#f0f0f0',
                        px: 2,
                        py: 0.5
                      }}
                    >
                      {currentLanguage === 'zh' ? '0-2年' : '0–2 years'}
                    </Typography>

                    <Box>
                      {[
                        { en: 'Niche focus: Power solutions for AI & data centers', zh: '专注细分市场：AI与数据中心电力解决方案' },
                        { en: 'Build engineering team (power + data center)', zh: '组建工程团队（电力+数据中心）' },
                        { en: '10–50 MW AI substation packages', zh: '10-50 MW AI变电站方案' },
                        { en: 'Modular substation for GPU cluster expansions', zh: 'GPU集群扩展模块化变电站' },
                        { en: 'Service contracts: maintenance + monitoring', zh: '服务合同：维护+监控' },
                      ].map((item, idx) => (
                        <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', mb: 1.5 }}>
                          <VerifiedIcon sx={{ fontSize: 18, mr: 1.5, mt: 0.3, color: '#000' }} />
                          <Typography variant="body2" sx={{ color: '#333', lineHeight: 1.6, fontSize: '14px' }}>
                            {currentLanguage === 'zh' ? item.zh : item.en}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </motion.div>
              </Grid>

              {/* Phase 2 */}
              <Grid item xs={12} md={6}>
                <motion.div variants={fadeIn}>
                  <Box
                    sx={{
                      p: { xs: 4, md: 5 },
                      height: '100%',
                      borderBottom: '1px solid rgba(0,0,0,0.08)',
                      transition: 'background-color 0.2s ease',
                      '&:hover': {
                        backgroundColor: '#f8f8f8',
                      }
                    }}
                  >
                    <Box
                      sx={{
                        width: 64,
                        height: 64,
                        backgroundColor: '#000',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 3,
                        color: '#fff',
                      }}
                    >
                      <SupportAgentIcon sx={{ fontSize: 32 }} />
                    </Box>

                    <Typography
                      variant="overline"
                      sx={{ color: '#666', letterSpacing: '0.1em', mb: 1, display: 'block' }}
                    >
                      {currentLanguage === 'zh' ? '第二阶段：扩展升级' : 'Phase 2: Expansion'}
                    </Typography>

                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 500,
                        mb: 1,
                        fontSize: { xs: '1.5rem', md: '1.75rem' },
                        letterSpacing: '-0.01em'
                      }}
                    >
                      {currentLanguage === 'zh' ? '数字化与AI服务' : 'Digital & AI-Based Services'}
                    </Typography>

                    <Typography
                      sx={{
                        color: '#000',
                        fontWeight: 600,
                        fontSize: '14px',
                        mb: 3,
                        display: 'inline-block',
                        backgroundColor: '#f0f0f0',
                        px: 2,
                        py: 0.5
                      }}
                    >
                      {currentLanguage === 'zh' ? '2-5年' : '2–5 years'}
                    </Typography>

                    <Box>
                      {[
                        { en: 'Transformer health scoring models', zh: '变压器健康评分模型' },
                        { en: 'Load forecasting for AI workloads', zh: '针对AI负载的电力预测模型' },
                        { en: 'Dashboards & predictive alarms', zh: '仪表盘与预测性警报' },
                        { en: 'Optimization algorithms', zh: '优化算法' },
                        { en: 'Transform into AI-native power company', zh: '转型为AI原生电力公司' },
                      ].map((item, idx) => (
                        <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', mb: 1.5 }}>
                          <VerifiedIcon sx={{ fontSize: 18, mr: 1.5, mt: 0.3, color: '#000' }} />
                          <Typography variant="body2" sx={{ color: '#333', lineHeight: 1.6, fontSize: '14px' }}>
                            {currentLanguage === 'zh' ? item.zh : item.en}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </motion.div>
              </Grid>
            </Grid>
          </motion.div>
        </Container>
      </Box>

      {/* Products Section - Clean card grid */}
      <Box id="products" sx={{ py: { xs: 10, md: 15 }, backgroundColor: '#f8f8f8' }}>
        <Container maxWidth="lg">
          <Box sx={{ mb: 8 }}>
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              variants={fadeIn}
            >
              <Typography
                variant="overline"
                sx={{
                  color: '#666',
                  mb: 2,
                  display: 'block',
                  letterSpacing: '0.15em'
                }}
              >
                {currentLanguage === 'zh' ? '产品' : 'PRODUCTS'}
              </Typography>
              <SectionTitle variant="h2" sx={{ fontSize: { xs: '2rem', md: '2.5rem' }, maxWidth: '600px' }}>
                {t('home.products.title')}
              </SectionTitle>
            </motion.div>
          </Box>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={staggerContainer}
          >
            <Grid container spacing={0}>
              {home.services.items.map((service, index) => {
                return (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <motion.div variants={fadeIn}>
                      <Box
                        onClick={() => handleOpenServiceDialog(service)}
                        sx={{
                          p: 4,
                          height: '100%',
                          minHeight: 300,
                          display: 'flex',
                          flexDirection: 'column',
                          cursor: 'pointer',
                          borderRight: { xs: 'none', sm: '1px solid rgba(0,0,0,0.08)' },
                          borderBottom: '1px solid rgba(0,0,0,0.08)',
                          transition: 'background-color 0.2s ease',
                          '&:hover': {
                            backgroundColor: '#f8f8f8',
                          },
                          '&:nth-of-type(3n)': {
                            borderRight: { md: 'none' }
                          }
                        }}
                      >
                        <ServiceIcon>
                          <Box
                            component="img"
                            src={service.image}
                            alt={currentLanguage === 'zh' ? service.title_zh : service.title}
                            sx={{
                              width: 80,
                              height: 80,
                              objectFit: 'cover',
                            }}
                          />
                        </ServiceIcon>
                        <Typography
                          variant="h6"
                          sx={{
                            mb: 2,
                            fontWeight: 500,
                            fontSize: '18px',
                            lineHeight: 1.3
                          }}
                        >
                          {currentLanguage === 'zh' ? service.title_zh : (service.title_en || service.title)}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: '#666',
                            mb: 3,
                            flexGrow: 1,
                            lineHeight: 1.6,
                            fontSize: '14px'
                          }}
                        >
                          {(() => {
                            const text = currentLanguage === 'zh' ? service.description_zh : (service.description_en || service.description);
                            const maxLength = 80;
                            if (text && text.length > maxLength) {
                              return text.substring(0, maxLength) + '...';
                            }
                            return text;
                          })()}
                        </Typography>
                        <Box sx={{ mt: 'auto', display: 'flex', alignItems: 'center' }}>
                          <Typography
                            sx={{
                              fontSize: '14px',
                              fontWeight: 500,
                              color: '#000',
                              display: 'flex',
                              alignItems: 'center',
                              '&:hover': { opacity: 0.7 }
                            }}
                          >
                            {currentLanguage === 'zh' ? '了解更多' : 'Learn more'}
                            <KeyboardArrowRightIcon sx={{ fontSize: 18, ml: 0.5 }} />
                          </Typography>
                        </Box>
                      </Box>
                    </motion.div>
                  </Grid>
                );
              })}
            </Grid>
          </motion.div>
        </Container>
      </Box>

      {/* Testimonials Section */}
      {/* <Box id="blog" sx={{ py: 10, backgroundColor: '#f5f5f7' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              variants={fadeIn}
            >
              <SectionTitle 
                variant="h3" 
                sx={{ 
                  '&::after': {
                    left: '50%',
                    transform: 'translateX(-50%)',
                  }
                }}
              >
                {currentLanguage === 'zh' ? home.testimonials.title_zh : home.testimonials.title}
              </SectionTitle>
              <SectionSubtitle 
                variant="h5"
                sx={{ maxWidth: '700px', mx: 'auto' }}
              >
                {currentLanguage === 'zh' ? home.testimonials.subtitle_zh : home.testimonials.subtitle}
              </SectionSubtitle>
            </motion.div>
          </Box>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={staggerContainer}
          >
            <Grid container spacing={4}>
              {home.testimonials.map((testimonial, index) => (
                <Grid item xs={12} md={4} key={index}>
                  <motion.div variants={fadeIn}>
                    <TestimonialCard>
                      <CardContent sx={{ p: 4 }}>
                        <Box sx={{ display: 'flex', mb: 3 }}>
                          <Avatar 
                            src={testimonial.image} 
                            alt={testimonial.name}
                            sx={{ width: 60, height: 60, mr: 2 }}
                          />
                          <Box>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                              {testimonial.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {currentLanguage === 'zh' ? 
                                (typeof testimonial.title_zh === 'string' ? testimonial.title_zh : '') : 
                                (typeof testimonial.title_en === 'string' ? testimonial.title_en : 
                                  (typeof testimonial.title === 'string' ? testimonial.title : ''))}
                            </Typography>
                            <Rating 
                              value={testimonial.rating} 
                              readOnly
                              size="small"
                              sx={{ mt: 0.5 }}
                            />
                          </Box>
                        </Box>
                        <Typography variant="body1" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                          "{currentLanguage === 'zh' ? 
                            (typeof testimonial.text_zh === 'string' ? testimonial.text_zh : '') : 
                            (typeof testimonial.text_en === 'string' ? testimonial.text_en : 
                              (typeof testimonial.text === 'string' ? testimonial.text : ''))}"
                        </Typography>
                      </CardContent>
                    </TestimonialCard>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </motion.div>
        </Container>
      </Box> */}

      {/* Projects Section */}
      {/* <Box id="projects" sx={{ py: 10, backgroundColor: '#fff' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              variants={fadeIn}
            >
              <SectionTitle 
                variant="h3" 
                sx={{ 
                  '&::after': {
                    left: '50%',
                    transform: 'translateX(-50%)',
                  }
                }}
              >
                {currentLanguage === 'zh' ? home.projects.title_zh : home.projects.title}
              </SectionTitle>
              <SectionSubtitle 
                variant="h5"
                sx={{ maxWidth: '700px', mx: 'auto' }}
              >
                {currentLanguage === 'zh' ? home.projects.subtitle_zh : home.projects.subtitle}
              </SectionSubtitle>
            </motion.div>
          </Box>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={staggerContainer}
          >
            <Grid container spacing={4}>
              {home.projects.items.map((project, index) => {
                return (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <motion.div variants={fadeIn}>
                      <ProjectCard onClick={() => handleOpenProjectDialog(project)} sx={{ cursor: 'pointer' }}>
                        <CardMedia
                          component="img"
                          image={project.image}
                          alt={currentLanguage === 'zh' ? project.title_zh : (project.title_en || project.title)}
                          sx={{ 
                            height: 280,
                            transition: 'transform 0.6s ease',
                          }}
                        />
                        <ProjectOverlay className="project-overlay">
                          <Typography 
                            variant="h5" 
                            sx={{ 
                              color: '#fff', 
                              fontWeight: 600,
                              mb: 1
                            }}
                          >
                            {currentLanguage === 'zh' ? project.title_zh : (project.title_en || project.title)}
                          </Typography>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: 'rgba(255, 255, 255, 0.8)',
                              mb: 3,
                              textAlign: 'center'
                            }}
                          >
                            {currentLanguage === 'zh' ? project.description_zh : (project.description_en || project.description)}
                          </Typography>
                          <Button
                            variant="outlined"
                            sx={{
                              borderColor: '#fff',
                              color: '#fff',
                              '&:hover': {
                                borderColor: '#fff',
                                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                              }
                            }}
                          >
                            {currentLanguage === 'zh' ? 
                              (typeof project.cta_zh === 'string' ? project.cta_zh : '查看全部') : 
                              (typeof project.cta_en === 'string' ? project.cta_en : 
                                (typeof project.cta === 'string' ? project.cta : 'View All'))}
                          </Button>
                        </ProjectOverlay>
                      </ProjectCard>
                    </motion.div>
                  </Grid>
                );
              })}
            </Grid>
          </motion.div>
        </Container>
      </Box> */}

      {/* CTA Section - Full width minimalist */}
      <Box id="contact">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8 }}
        >
          <ContactSection>
            <Container maxWidth="lg">
              <Grid container spacing={6} alignItems="center">
                <Grid item xs={12} md={8}>
                  <Typography
                    variant="h2"
                    sx={{
                      fontWeight: 500,
                      letterSpacing: '-0.02em',
                      fontSize: { xs: '2rem', md: '3rem' },
                      mb: 2
                    }}
                  >
                    {currentLanguage === 'zh' ? home.contact.title_zh : (home.contact.title_en || home.contact.title)}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: 'rgba(255,255,255,0.7)',
                      fontSize: '16px',
                      maxWidth: '500px'
                    }}
                  >
                    {currentLanguage === 'zh' ? home.contact.description_zh : (home.contact.description_en || home.contact.description)}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4} sx={{ textAlign: { xs: 'left', md: 'right' } }}>
                  <Button
                    onClick={handleOpenContactModal}
                    variant="contained"
                    size="large"
                    sx={{
                      px: 5,
                      py: 1.5,
                      fontSize: '14px',
                      fontWeight: 500,
                      backgroundColor: '#fff',
                      color: '#000',
                      borderRadius: 0,
                      '&:hover': {
                        backgroundColor: 'rgba(255,255,255,0.9)',
                      },
                      transition: 'all 0.2s ease'
                    }}
                  >
                    {currentLanguage === 'zh' ?
                      (typeof home.contact.cta_zh === 'string' ? home.contact.cta_zh : '联系我们') :
                      (typeof home.contact.cta_en === 'string' ? home.contact.cta_en :
                        (typeof home.contact.cta === 'string' ? home.contact.cta : 'Contact Us'))}
                  </Button>
                </Grid>
              </Grid>
            </Container>
          </ContactSection>
        </motion.div>
      </Box>

      {/* Service Detail Dialog - Minimalist style */}
      <Dialog
        open={serviceDialogOpen}
        onClose={handleCloseServiceDialog}
        maxWidth="md"
        fullWidth
        scroll="paper"
        PaperProps={{
          sx: {
            borderRadius: 0,
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          }
        }}
      >
        {selectedService && (
          <>
            <DialogTitle sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
              p: 3,
              bgcolor: '#fff'
            }}>
              <Typography
                variant="h5"
                component="div"
                sx={{ fontWeight: 500, letterSpacing: '-0.01em' }}
              >
                {currentLanguage === 'zh' ? selectedService.title_zh : (selectedService.title_en || selectedService.title)}
              </Typography>
              <IconButton
                edge="end"
                color="inherit"
                onClick={handleCloseServiceDialog}
                aria-label="close"
                sx={{
                  color: '#000',
                  '&:hover': { backgroundColor: 'transparent', opacity: 0.6 }
                }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ p: 0 }}>
              <CardMedia
                component="img"
                image={selectedService.image}
                alt={currentLanguage === 'zh' ? selectedService.title_zh : (selectedService.title_en || selectedService.title)}
                sx={{
                  height: 350,
                  objectFit: 'cover',
                  backgroundColor: '#f8f8f8'
                }}
              />
              <Box sx={{ p: 4 }}>
                {(selectedService.fullDescription_zh || selectedService.fullDescription_en || selectedService.fullDescription) && (
                  <Box sx={{ mb: 4 }}>
                    <Typography variant="body1" sx={{ color: '#333', lineHeight: 1.8 }}>
                      {currentLanguage === 'zh'
                        ? (selectedService.fullDescription_zh || '暂无详细内容')
                        : (selectedService.fullDescription_en || selectedService.fullDescription || 'No detailed content available')}
                    </Typography>
                  </Box>
                )}

                <Box sx={{ display: 'flex', justifyContent: 'flex-start', pt: 2 }}>
                  <Button
                    variant="contained"
                    onClick={handleOpenContactModal}
                    size="large"
                    sx={{
                      backgroundColor: '#000',
                      color: '#fff',
                      borderRadius: 0,
                      px: 4,
                      py: 1.5,
                      fontSize: '14px',
                      fontWeight: 500,
                      '&:hover': {
                        backgroundColor: '#333'
                      }
                    }}
                  >
                    {currentLanguage === 'zh' ? 
                      (typeof home.contact.cta_zh === 'string' ? home.contact.cta_zh : '联系我们') : 
                      (typeof home.contact.cta_en === 'string' ? home.contact.cta_en : 
                        (typeof home.contact.cta === 'string' ? home.contact.cta : 'Contact Us'))}
                  </Button>
                </Box>
              </Box>
            </DialogContent>
          </>
        )}
      </Dialog>

      {/* Contact Modal */}
      <ContactModal 
        open={contactModalOpen} 
        onClose={handleCloseContactModal} 
      />

      {/* Project Detail Dialog */}
      <Dialog 
        open={projectDialogOpen} 
        onClose={handleCloseProjectDialog}
        maxWidth="md"
        fullWidth
        scroll="paper"
      >
        {selectedProject && (
          <>
            <DialogTitle sx={{ 
              display: 'flex', 
              justifyContent: 'space-between',
              alignItems: 'center',
              borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
              p: 2
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="h5" component="div">
                  {currentLanguage === 'zh' ? selectedProject.title_zh : (selectedProject.title_en || selectedProject.title)}
                </Typography>
              </Box>
              <IconButton 
                edge="end" 
                color="inherit" 
                onClick={handleCloseProjectDialog} 
                aria-label="close"
                sx={{ color: 'text.secondary' }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers sx={{ p: 0 }}>
              <CardMedia
                component="img"
                image={selectedProject.image}
                alt={currentLanguage === 'zh' ? selectedProject.title_zh : (selectedProject.title_en || selectedProject.title)}
                sx={{ 
                  height: 300, 
                  objectFit: 'contain',
                  backgroundColor: '#f5f5f5'
                }}
              />
              <Box sx={{ p: 3 }}>
                <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
                  {currentLanguage === 'zh' ? selectedProject.description_zh : (selectedProject.description_en || selectedProject.description)}
                </Typography>
                
                <Divider sx={{ my: 3 }} />
                
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  {currentLanguage === 'zh' ? '项目详情' : 'Project Details'}
                </Typography>
                
                {/* 确保渲染不会因为内容不存在而出错 */}
                {currentLanguage === 'zh' ? (
                  <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8 }}>
                    {selectedProject.fullDescription_zh || selectedProject.description_zh || '暂无详细内容'}
                  </Typography>
                ) : (
                  <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8 }}>
                    {selectedProject.fullDescription_en || selectedProject.fullDescription || selectedProject.description || 'No detailed content available'}
                  </Typography>
                )}
                
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                  <Button 
                    variant="contained" 
                    onClick={handleOpenContactModal}
                    size="large"
                  >
                    {currentLanguage === 'zh' ? 
                      (typeof home.contact.cta_zh === 'string' ? home.contact.cta_zh : '联系我们') : 
                      (typeof home.contact.cta_en === 'string' ? home.contact.cta_en : 
                        (typeof home.contact.cta === 'string' ? home.contact.cta : 'Contact Us'))}
                  </Button>
                </Box>
              </Box>
            </DialogContent>
          </>
        )}
      </Dialog>
    </Box>
  );
}

export default Home; 